import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { foundation } from '../../ui/tokens';
import {
  confirmDialogPanel,
  overlayTitle,
  overlayBody,
} from '../../ui/overlayStyles';
import { confirmDialogFlexShell } from '../../ui/confirmDialogStyles';
import { overlayLayers } from '../../ui/overlayLayers';
import { useModalKeyboard } from '../../hooks/ui/useModalKeyboard';
import { useConfirmGate, CONFIRM_GATE } from '../../hooks/ui/useConfirmGate';
import { useModalHandoffEnter } from '../../hooks/ui/useModalHandoffEnter';
import AnimatedModalShell from './AnimatedModalShell';
import ConfirmPhraseField from './ConfirmPhraseField';
import ConfirmFreeTextField from './ConfirmFreeTextField';
import useConfirmFreeTextState from './useConfirmFreeTextState';
import { useBulkStyleSendExit } from '../../hooks/ui/useBulkStyleSendExit';
import { useModalActionBeat } from '../../hooks/ui/useModalActionBeat';
import ConfirmDialogActions from './ConfirmDialogActions';
import ConfirmDialogBody from './ConfirmDialogBody';
import ConfirmDialogHeader from './ConfirmDialogHeader';
import { resolveConfirmBeat } from './actionBeat/resolveConfirmBeat';


const { spacePx } = foundation;

/**
 * Reusable styled confirmation dialog (portal-rendered).
 *
 * Props:
 *  - title          – dialog heading
 *  - message        – body text
 *  - confirmLabel   – confirm button text (default "Confirm")
 *  - cancelLabel    – cancel button text (default "Cancel")
 *  - tone           – "default" | "danger" (changes confirm button style)
 *  - requireInput   – when set (e.g. "DELETE"), user must type this string to enable confirm
 *  - freeTextInput  – optional free-text field (e.g. rename). Mutually exclusive with requireInput for UI.
 *                     onConfirm receives { text: string } when used.
 *                     Optional `validate(trimmed) → { ok, helper?, tone? }` soft-gates confirm
 *                     and renders a live helper under the field (money confirms).
 *                     Optional `maxValue` (number) swaps the field to MoneyInput with the
 *                     same shake-on-rejection + digit sanitization as invoice line prices —
 *                     keystrokes / pastes above the cap never land in the value.
 *  - attestation    – { notice, label }: amber verify panel + a required checkbox that gates
 *                     confirm. For security actions done on someone's behalf (e.g. clearing a
 *                     user's MFA) where the admin must attest they verified the request.
 *  - onConfirm()    – called when user confirms; may receive { text } when freeTextInput is set
 *  - onCancel()     – called when user cancels / clicks scrim / presses Esc
 *  - secondaryAction – optional label for a THIRD choice (e.g. "Send without credit"),
 *                     rendered as a text LINK above the [Cancel][Confirm] row (KAN-948: the
 *                     one overlay footer). Requires `onSecondary`. Mirrors ConfirmDialogInline
 *                     — omit both for the ordinary 2-button dialog (unchanged for every
 *                     other caller).
 *  - onSecondary()  – called when the secondary button is clicked (after exit animation)
 */
export default function ConfirmDialog({
  // Renamed at destructure (mechanical UI sweep) — mirrors the
  // confirmLabel/cancelLabel treatment just below: the localized default is
  // resolved after `useIntl()` is available, so only an omitted `title`
  // (undefined) gets it. An explicit title (the vast majority of callers)
  // always wins.
  title: titleProp,
  // Same treatment as title — hardcoded 'Are you sure?' leaked English into
  // ES dialogs whenever a caller omitted message.
  message: messageProp,
  // Renamed at destructure (item 3, closing fix pass) — the localized
  // default is resolved below, after `useIntl()` is available. Only an
  // omitted prop (undefined) gets the localized default; an explicit
  // falsy value (null/'') still hides the confirm/cancel button per the
  // `canConfirm` contract, so callers relying on that keep working.
  confirmLabel: confirmLabelProp,
  cancelLabel: cancelLabelProp,
  tone = 'default',
  requireInput,
  freeTextInput,
  attestation,
  onConfirm,
  onCancel,
  bodyNode,
  leadingIcon,
  requireInputCaseInsensitive = false,
  busy = false,
  confirmingLabel,
  zIndex = overlayLayers.confirmDialog,
  /** Use desktop scale-in on mobile (session/logout confirms). Default slide-up on touch. */
  panelMotion = 'auto',
  /**
   * When true, onConfirm fires immediately on click (before exit animation).
   * Parent should set `busy` while work runs, then unmount. Use for security
   * actions where every millisecond before the server revoke matters.
   */
  confirmImmediate = false,
  secondaryAction,
  onSecondary,
  /**
   * Opt-in ACTION BEAT (founder 2026-08-19): { workingLabel, successLabel }.
   * When set, confirm runs progress → success-hold → morph-close
   * (useModalActionBeat) instead of closing immediately — onConfirm's
   * returned promise/boolean drives it (a `false` return = failure, closes
   * silently, no in-modal error — toasts/row-state carry the message). The
   * footer swaps to ModalActionBeat in the SAME slot (stable-layout rule)
   * and Esc is gated while it runs. Absent (the default for ~50 existing
   * callers), behavior is byte-identical to before — the two hooks below
   * stay dormant since their exit path is only reachable through beat.run().
   */
  actionBeat,
  /**
   * When set with `actionBeat` + `onSecondary`, the third button also plays
   * the beat (credit-eligible mark-as-sent). Absent, secondary still dismisses
   * first — lifecycle "bill anyway" must not inherit a write beat.
   */
  actionBeatOnSecondary = false,
  /**
   * Which GATE stands between the button and the action:
   *   'none'    single confirm — reversible, low-consequence
   *   'phrase'  type a word or a name (today's behaviour)
   *   'morph'   two-step: tap arms, tap again commits
   * Omitted, it is derived from `requireInput`, so every existing caller keeps
   * the exact gate it has today. See useConfirmGate.
   */
  gate,
  /** Required by `gate="morph"` — the armed label, naming what will happen. */
  step2Label,
  /**
   * Task 2 — ONE checking state (founder standing order via WAVE 1 · ENGINE,
   * 2026-09-24): a confirm that needs an async pre-check (readiness, an
   * invoiced check, …) opens IMMEDIATELY in this state instead of blocking
   * before open (no affordance for up to 8s) or opening in a provisional
   * shape that swaps under the user. While `checking`, no gate UI renders —
   * no phrase field, no morph arming, no attestation checkbox — the confirm
   * button is DISABLED showing `checkingLabel`; Cancel/Esc/scrim stay live.
   * See `useConfirmationDialog`'s `prepare` for the usual way this gets set.
   */
  checking = false,
  checkingLabel: checkingLabelProp,
  /** Opt-in modal hand-off RECEIVER key (modalHandoff.js): consumes a staged sender rect, morphs from it. */
  handoffKey,
}) {
  const intl = useIntl();
  const handoff = useModalHandoffEnter(handoffKey);
  // Localized label defaults (item 3, closing fix pass) — `common.confirm` /
  // `common.cancel` already exist in both catalogs. Every reference below
  // keeps using `confirmLabel`/`cancelLabel` unchanged.
  const confirmLabel = confirmLabelProp === undefined
    ? intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })
    : confirmLabelProp;
  const cancelLabel = cancelLabelProp === undefined
    ? intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })
    : cancelLabelProp;
  // Same treatment for the default title (mechanical UI sweep, item 3).
  // `common.pleaseConfirm` exists in BOTH catalogs (EN+ES) — react-intl
  // falls back to defaultMessage below until the orchestrator adds it
  // (EN "Please confirm" / ES "Confirma la acción").
  const title = titleProp === undefined
    ? intl.formatMessage({ id: 'common.pleaseConfirm', defaultMessage: 'Please confirm' })
    : titleProp;
  // Explicit null/'' still hides the body <p> below — only an OMITTED
  // message gets the localized default.
  const message = messageProp === undefined
    ? intl.formatMessage({ id: 'common.areYouSure', defaultMessage: 'Are you sure?' })
    : messageProp;
  const checkingLabel = checkingLabelProp === undefined
    ? intl.formatMessage({ id: 'common.checking', defaultMessage: 'Checking…' })
    : checkingLabelProp;
  // "mounted ONLY for dialogs opened in checking mode" — captured once, at
  // this instance's first render, never re-read. A dialog that never opens
  // checking never pays for the extra AutoHeight wrapper (byte-identical).
  const openedCheckingRef = useRef(checking);
  const [inputValue, setInputValue] = useState('');
  const [attested, setAttested] = useState(false);
  const [animOpen, setAnimOpen] = useState(true);
  const exitActionRef = useRef(null);
  const hasPendingExitRef = useRef(false);
  // Opt-in action beat (see the actionBeat doc above). Dormant when
  // actionBeat is absent — playExit is only reachable via beat.run(), which
  // runConfirm only calls when actionBeat is set.
  const { sendMorph, sendMorphExitStyle, playExit, reduced } = useBulkStyleSendExit({ exitActionRef, hasPendingExitRef, setAnimOpen });
  // WAVE 1 · INTEGRATION-A: resolveConfirmBeat picks RING / FILL / legacy bar
  // from `actionBeat`'s shape — recomputed each render (pure, cheap) since
  // callers pass a fresh object literal every time anyway.
  const beatCfg = resolveConfirmBeat(actionBeat, { danger: tone === 'danger' });
  const beat = useModalActionBeat({ playExit, onClose: onCancel, reduced, ...beatCfg.timing });
  const hasActionBeat = Boolean(actionBeat);

  const readyRef = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => { readyRef.current = true; }, 200);
    return () => clearTimeout(t);
  }, []);

  const requestDismiss = useCallback((action) => {
    if (!readyRef.current) return;
    if (busy || beat.isWorking) return;
    exitActionRef.current = action;
    hasPendingExitRef.current = true;
    setAnimOpen(false);
  }, [busy, beat.isWorking]);

  const handleExitComplete = useCallback(() => {
    if (!hasPendingExitRef.current) return;
    hasPendingExitRef.current = false;
    exitActionRef.current?.();
  }, []);

  const guardedCancel = useCallback(() => {
    requestDismiss(onCancel);
  }, [requestDismiss, onCancel]);

  const hasSecondary = Boolean(secondaryAction) && typeof onSecondary === 'function';
  const runSecondary = useCallback(() => {
    if (hasActionBeat && actionBeatOnSecondary) {
      beat.run(() => onSecondary());
      return;
    }
    requestDismiss(onSecondary);
  }, [hasActionBeat, actionBeatOnSecondary, beat, requestDismiss, onSecondary]);

  const mouseDownOnScrimRef = useRef(false);
  const handleScrimMouseDown = useCallback((e) => {
    mouseDownOnScrimRef.current = e.target === e.currentTarget;
  }, []);
  const handleScrimClick = useCallback((e) => {
    if (mouseDownOnScrimRef.current && e.target === e.currentTarget) {
      e.stopPropagation();
      guardedCancel();
    }
    mouseDownOnScrimRef.current = false;
  }, [guardedCancel]);

  const inputRequired = typeof requireInput === 'string' && requireInput.length > 0;
  const hasFreeText = Boolean(freeTextInput);

  // Keyboard-sized height cap (touch only, only while a keyboard-owning field
  // shows) + free-text evaluation: see useConfirmFreeTextState.
  const hasTypedField = inputRequired || hasFreeText;
  const trimmed = inputValue.trim();
  const {
    hasPresets, setOtherOpen, summonHeightCap, freeTextOk, requiredHelper,
    setAttempted, shownFreeTextEval,
  } = useConfirmFreeTextState({ freeTextInput, inputRequired, trimmed });
  const attestationOk = attestation ? attested : true;

  // ── The gate ───────────────────────────────────────────────────────────────
  // `commit` is stable and reads runConfirm through a ref, because the gate
  // needs a commit callback while `canConfirm` needs the gate's own state —
  // the two would otherwise be circular.
  const runConfirmRef = useRef(null);
  const commit = useCallback(() => { runConfirmRef.current?.(); }, []);
  const resolvedGateMode = gate
    || (inputRequired ? CONFIRM_GATE.PHRASE : CONFIRM_GATE.NONE);
  const confirmGate = useConfirmGate({
    mode: resolvedGateMode,
    phrase: requireInput,
    phraseCaseInsensitive: requireInputCaseInsensitive,
    // `!checking` blocks arming outright while a pre-check is in flight — the
    // ONLY path to `activate()` is the document-level Enter binding below
    // (the footer renders no clickable gate UI during checking at all), and
    // this closes it at the source rather than relying on the footer swap.
    enabled: !checking && attestationOk && (hasFreeText ? freeTextOk : true),
    reduced,
    onCommit: commit,
  });
  // `confirmLabel` is load-bearing, not decoration: a falsy label removes the
  // confirm BUTTON, and the document-level Enter binding below must go with it.
  // Without this, a caller that hides the button while it resolves something
  // (e.g. the bulk-delete readiness check) still ships a live keyboard path —
  // Enter fires runConfirm with no affordance and no typed phrase on screen.
  // No confirm affordance ⇒ no confirm.
  // `gate.satisfied` folds in all three modes: the typed phrase matches, the
  // morph is armed, or there is no gate. Free text is data capture, not a
  // gate, and is only consulted when no phrase is required — the original
  // mutually-exclusive semantics, unchanged.
  const canConfirm = Boolean(confirmLabel)
    && !checking
    && confirmGate.satisfied
    && (hasFreeText && !inputRequired ? freeTextOk : true)
    && attestationOk;

  const runConfirm = useCallback(() => {
    if (!canConfirm || busy) return;
    if (hasActionBeat) {
      // The beat owns the lifecycle: working → success hold → morph close.
      // onConfirm's returned promise/boolean drives it (see actionBeat doc).
      beat.run(() => (hasFreeText ? onConfirm({ text: trimmed }) : onConfirm()));
      return;
    }
    if (confirmImmediate) {
      if (hasFreeText) onConfirm({ text: trimmed });
      else onConfirm();
      return;
    }
    if (hasFreeText) requestDismiss(() => onConfirm({ text: trimmed }));
    else requestDismiss(onConfirm);
  }, [canConfirm, busy, hasActionBeat, beat, hasFreeText, requestDismiss, onConfirm, trimmed, confirmImmediate]);

  useEffect(() => { runConfirmRef.current = runConfirm; }, [runConfirm]);

  // Enter goes through the SAME activate() the button does. Never bind it to
  // runConfirm directly: a multi-step gate would be bypassed in one press, and
  // that is reachable on touch (an iPad with a keyboard is `pointer: coarse`).
  // `e.repeat` is rejected so a HELD Enter can't arm and then commit itself
  // once the inert window lapses.
  const activateGate = useCallback(() => {
    if (requiredHelper && !freeTextOk && attestationOk && !checking) setAttempted(true);
    confirmGate.activate();
  }, [requiredHelper, freeTextOk, attestationOk, checking, confirmGate, setAttempted]);
  const onEnterKey = useCallback((e) => {
    if (e?.repeat) return;
    // Enter on a focused reason row only PICKS it - never also confirms.
    if (document.activeElement?.closest?.('[role="radiogroup"]')) return;
    activateGate();
  }, [activateGate]);

  useModalKeyboard(animOpen && !busy && !beat.isWorking, { onEscape: guardedCancel, onEnter: onEnterKey });

  useEffect(() => {
    if (hasFreeText) {
      setInputValue(freeTextInput.initialValue ?? '');
    } else {
      setInputValue('');
    }
  }, [hasFreeText, freeTextInput?.initialValue]);

  const messageStyle = {
    ...overlayBody,
    marginBottom: bodyNode ? spacePx[3] : (hasTypedField ? spacePx[2] : spacePx[4]),
  };

  // *** RESERVE THE ✕ GUTTER IN THE SHELL, NOT BY SHORTENING COPY. ***
  // The morph gate's corner ✕ is the shared `OverlayCloseButton` — absolute, a
  // 44px tap area over the panel's top-right corner (its 36px round face sits
  // 16px in from the edges, KAN-948). Neither shell ever reserved horizontal room for it, so a
  // title wider than ~259px runs UNDER it. Measured, the Spanish "Devolver el
  // presupuesto a Enviado" overlaps at both 375 and 390 (the English does not,
  // which is why it went unnoticed) — and the ✕ only becomes opaque when the
  // gate ARMS, so the title is obscured at exactly the moment it is re-read.
  // Spanish is the primary locale here, so this is the default rendering.
  // Applied in the shell so every current and future morph caller is covered.
  const morphTitleGutter = confirmGate.isMorph ? { paddingRight: 44 } : null;

  // Panel content (header + scroll body + typed fields + action row). KAN-1078: no ring
  // stage wraps it any more - every write plays the Fill beat inside the footer.
  const freeTextNode = (
    <ConfirmFreeTextField
      freeTextInput={freeTextInput}
      value={inputValue}
      onChange={setInputValue}
      evaluation={shownFreeTextEval}
      busy={busy}
      onFreeformChange={setOtherOpen}
    />
  );
  const panelContent = (
    <>
      <ConfirmDialogHeader
        leadingIcon={leadingIcon}
        tone={tone}
        title={title}
        titleStyle={titleStyle}
        morphTitleGutter={morphTitleGutter}
      />

      <ConfirmDialogBody
        message={message}
        messageStyle={messageStyle}
        bodyNode={bodyNode}
        hasTypedField={hasTypedField}
        openedChecking={openedCheckingRef.current}
        checking={checking}
        attestation={attestation}
        attested={attested}
        setAttested={setAttested}
        busy={busy}
      >
        {!checking && hasFreeText && !inputRequired && hasPresets ? freeTextNode : null}
      </ConfirmDialogBody>

      {!checking && inputRequired && (
        <ConfirmPhraseField
          phrase={requireInput}
          value={confirmGate.phraseValue}
          onChange={confirmGate.setPhraseValue}
          caseInsensitive={requireInputCaseInsensitive}
        />
      )}
      {!checking && hasFreeText && !inputRequired && !hasPresets && freeTextNode}

      <ConfirmDialogActions
        hasActionBeat={hasActionBeat}
        beat={beat}
        actionBeat={actionBeat}
        beatKind={beatCfg.kind}
        fillTone={beatCfg.fill?.tone}
        reduced={reduced}
        tone={tone}
        hasTypedField={hasTypedField}
        cancelLabel={cancelLabel}
        guardedCancel={guardedCancel}
        hasSecondary={hasSecondary}
        secondaryAction={secondaryAction}
        runSecondary={runSecondary}
        confirmLabel={confirmLabel}
        canConfirm={canConfirm}
        runConfirm={runConfirm}
        checking={checking}
        checkingLabel={checkingLabel}
        busy={busy}
        confirmingLabel={confirmingLabel}
        morph={confirmGate.isMorph ? {
          armed: confirmGate.armed,
          activate: activateGate,
          step2Label,
          enabled: attestationOk && (hasFreeText ? (freeTextOk || Boolean(requiredHelper)) : true),
          reduced,
        } : undefined}
      />
    </>
  );

  return (
    <AnimatedModalShell
      isOpen={animOpen}
      onExitComplete={handleExitComplete}
      morphActive={sendMorph}
      morphExitStyle={sendMorphExitStyle}
      onScrimClick={handleScrimClick}
      onScrimMouseDown={handleScrimMouseDown}
      zIndex={zIndex}
      panelMotion={panelMotion}
      morphEnterFromRect={handoff.morphEnterFromRect}
      skipEnter={handoff.skipEnter}
      dialogStyle={summonHeightCap ? { ...dialogPanelStyle, ...summonHeightCap } : dialogPanelStyle}
      dialogProps={{
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': title,
        'data-testid': 'confirm-dialog',
        // The confirm card's fields are all pinned chrome — the inline
        // keyboard guard scrolling (or spacer-padding) anything inside this
        // card is always wrong (the white-void capture, 2026-08-21).
        // Positioning (band-centering + anticipation) is this family's whole
        // keyboard strategy.
        'data-cvx-kb-guard-exempt': '',
      }}
    >
      {panelContent}
    </AnimatedModalShell>
  );
}

const dialogPanelStyle = {
  ...confirmDialogPanel,
  ...confirmDialogFlexShell,
  // Positioning context for the morph gate's corner ✕. Harmless for every
  // other caller — nothing else in this card is absolutely positioned.
  position: 'relative',
};

const titleStyle = {
  ...overlayTitle,
  flexShrink: 0,
};
